/**
 * Global hook for breadcrumb filters (Business, Contract, Date)
 *
 * This hook provides a reusable way to manage business, contract, and date filters
 * that are displayed in the breadcrumb bar. It handles:
 * - Fetching businesses from API
 * - Loading contracts when business changes
 * - Persisting filter state in Redux
 * - Generating breadcrumb actions for the Breadcrumb component
 *
 */

import React, { useEffect, useState, useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { Button, Modal, notification } from "antd"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { setLoader, setFilters } from "store/actions"

export interface BreadcrumbFiltersState {
	business: string | null
	contract: string | null
	date: moment.Moment | null
}

export interface UseBreadcrumbFiltersOptions {
	businessEndpoint?: string
	autoSelectFirst?: boolean // Auto-select first business/contract on load
	onDeleteModalSuccess?: () => void
	// Adds an "All contracts" option to the contract select. Opt-in — only
	// screens that can actually handle contract === "all" (no single
	// contractDetails, no contract-scoped actions) should enable this.
	allowAllContracts?: boolean
}

export const ALL_CONTRACTS_VALUE = "all"

export interface UseBreadcrumbFiltersReturn {
	// Data
	businesses: any[]
	contracts: any[]
	filters: BreadcrumbFiltersState

	// Handlers
	handleBusinessChange: (businessId: string | null) => void
	handleContractChange: (contractId: string | null, contracts: any[]) => void
	handleDateChange: (date: moment.Moment | null) => void

	// Breadcrumb actions (ready to use in setBreadcrumb)
	breadcrumbActions: any[]

	// Modal element to render in the page
	deleteModal: React.ReactNode

	// Utilities
	isLoading: boolean
	refetch: () => void
}

export const useBreadcrumbFilters = (
	options: UseBreadcrumbFiltersOptions
): UseBreadcrumbFiltersReturn => {
	const {
		autoSelectFirst = true,
		onDeleteModalSuccess,
		allowAllContracts = false,
	} = options

	const dispatch = useDispatch()

	// Get persisted filters from Redux
	const persistedFilters = useSelector((state: any) => state.filters) || {}

	// Extract values for memoization dependencies
	const businessValue = persistedFilters.business
	const contractValue = persistedFilters.contract
	const dateValue = persistedFilters.date

	// Local state
	const [businesses, setBusinesses] = useState<any[]>([])
	const [contracts, setContracts] = useState<any[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [modalVisible, setModalVisible] = useState(false)

	// Filter state from Redux - memoized to prevent unnecessary re-renders.
	// "All contracts" is only meaningful for screens that opted into it — on
	// every other screen, a persisted "all" (e.g. left over from Routes,
	// since the filter is shared across screens) is treated as no contract.
	const filters: BreadcrumbFiltersState = useMemo(
		() => ({
			business: businessValue || null,
			contract:
				!allowAllContracts && contractValue === ALL_CONTRACTS_VALUE
					? null
					: contractValue || null,
			date: dateValue ? moment.utc(dateValue) : moment.utc().startOf("day"),
		}),
		[businessValue, contractValue, dateValue, allowAllContracts]
	)

	// Helper to extract contract details for Redux
	const getContractDetails = (contract: any) => {
		// If any of active inside
		const allowExternalDelivery = Object.values(
			contract?.externalDelivery || {}
		).some((delivery: any) => delivery?.active)

		return {
			name: contract.name,
			functionalities: contract.functionalities,
			type: contract.type?.type,
			optimizer: contract.type?.optimizer,
			allowExternalDelivery: allowExternalDelivery,
		}
	}

	// Fetch a specific business with contracts
	const fetchContracts = useCallback(async (businessId: string) => {
		try {
			const url = Endpoints.v2.uriBusinesses({
				businessId,
				includeContracts: true,
			})
			const response = await API.get({ url })

			if (response.ok) {
				const results = response.data.results
				const business = results.length > 0 ? results[0] : null
				const contracts = business?.contracts || []
				return contracts
			}
		} catch (error) {
			console.error("Error fetching business with contracts:", error)
		}
		return []
	}, [])

	// Fetch businesses without contracts (lightweight)
	const fetchBusinesses = useCallback(async () => {
		setIsLoading(true)
		dispatch(setLoader(true))

		try {
			// Get current filter values
			const currentBusiness = businessValue
			const currentDate = dateValue
				? moment.utc(dateValue)
				: moment.utc().startOf("day")

			// If business is already selected, fetch businesses and contracts in parallel
			if (currentBusiness) {
				const [businessesResponse, businessContracts] = await Promise.all([
					API.get({ url: Endpoints.v2.uriBusinesses() }),
					fetchContracts(currentBusiness),
				])

				if (businessesResponse.ok) {
					const fetchedBusinesses = businessesResponse.data.results || []
					setBusinesses(fetchedBusinesses)
					setContracts(businessContracts)
				}
			} else {
				// No business selected, just fetch businesses (no contracts)
				const url = Endpoints.v2.uriBusinesses()
				const response = await API.get({ url })

				if (response.ok) {
					const fetchedBusinesses = response.data.results || []
					setBusinesses(fetchedBusinesses)

					// Auto-select first business if enabled
					if (autoSelectFirst && fetchedBusinesses.length > 0) {
						const firstBusiness = fetchedBusinesses[0]

						// Fetch contracts for first business
						const firstBusinessContracts = await fetchContracts(
							firstBusiness._id
						)
						setContracts(firstBusinessContracts)

						const firstContract =
							firstBusinessContracts.length > 0
								? firstBusinessContracts[0]
								: null

						// Update Redux with auto-selected values
						dispatch(
							setFilters({
								business: firstBusiness._id,
								contract: firstContract?._id || null,
								contractDetails: firstContract
									? getContractDetails(firstContract)
									: null,
								date: currentDate,
							})
						)
					}
				}
			}
		} catch (error) {
			console.error("Error fetching businesses:", error)
		} finally {
			setIsLoading(false)
			dispatch(setLoader(false))
		}
	}, [autoSelectFirst, dispatch, businessValue, dateValue, fetchContracts])

	// Fetch businesses on mount only
	useEffect(() => {
		fetchBusinesses()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		// Re-hydrate contractDetails from fresh data whenever contracts list changes to prevent stale data in Redux
		const freshContract = contracts.find((c: any) => {
			return c._id === contractValue
		})
		const freshContractDetails = freshContract
			? getContractDetails(freshContract)
			: null
		const hasChanged =
			JSON.stringify(freshContractDetails) !==
			JSON.stringify(persistedFilters.contractDetails)

		if (hasChanged) {
			dispatch(
				setFilters({
					...persistedFilters,
					contractDetails: freshContractDetails,
				})
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contracts])

	// Handlers
	const handleBusinessChange = useCallback(
		async (businessId: string | null) => {
			// Get current date from Redux to avoid stale closure
			const currentDate = persistedFilters?.date
				? moment.utc(persistedFilters.date)
				: moment.utc().startOf("day")

			if (!businessId) {
				// Clear contracts if no business selected
				setContracts([])
				dispatch(
					setFilters({
						business: null,
						contract: null,
						contractDetails: null,
						date: currentDate,
					})
				)
				return
			}

			dispatch(
				setFilters({
					business: businessId,
					contract: null,
					contractDetails: null,
					date: currentDate,
				})
			)

			// Fetch contracts for the selected business
			const newContracts = await fetchContracts(businessId)
			setContracts(newContracts)

			// Auto-select first contract if available
			const newContract =
				autoSelectFirst && newContracts.length > 0 ? newContracts[0] : null

			newContract &&
				dispatch(
					setFilters({
						business: businessId,
						contract: newContract?._id || null,
						contractDetails: newContract
							? getContractDetails(newContract)
							: null,
						date: currentDate,
					})
				)
		},
		[autoSelectFirst, dispatch, persistedFilters, fetchContracts]
	)

	const handleContractChange = useCallback(
		(contractId: string | null, contracts: any[]) => {
			// Get current values from Redux to avoid stale closure
			const currentBusiness = persistedFilters?.business
			const currentDate = persistedFilters?.date
				? moment.utc(persistedFilters.date)
				: moment.utc().startOf("day")

			const selectedContract = contracts.find((c) => c._id === contractId)

			dispatch(
				setFilters({
					business: currentBusiness,
					contract: contractId,
					contractDetails: selectedContract
						? getContractDetails(selectedContract)
						: null,
					date: currentDate,
				})
			)
		},
		[dispatch, persistedFilters]
	)

	const handleDateChange = useCallback(
		(date: moment.Moment | null) => {
			dispatch(
				setFilters({
					...persistedFilters,
					date: date,
				})
			)
		},
		[dispatch, persistedFilters]
	)

	// Generate breadcrumb actions - memoized to prevent unnecessary re-renders
	const breadcrumbActions = useMemo(() => {
		const actions = []

		// Add delete button if enabled (for dev/staging environments)
		if (onDeleteModalSuccess) {
			const isDevOrStaging =
				process.env.ENVIRONMENT === undefined ||
				process.env.ENVIRONMENT === null ||
				process.env.ENVIRONMENT === "local" ||
				process.env.ENVIRONMENT === "dev" ||
				process.env.ENVIRONMENT === "stag"

			if (isDevOrStaging) {
				actions.push({
					type: "button",
					text: "💥",
					className: "BreadcrumbDeleteButton",
					onClick: () => setModalVisible(true),
					disabled:
						!filters.business ||
						!filters.contract ||
						filters.contract === ALL_CONTRACTS_VALUE ||
						!filters.date,
					margin: "right",
				})
			}
		}

		// Business select
		actions.push({
			type: "select",
			text: Strings.businesses.business,
			placeholder: Strings.orders.selectBusiness,
			value: filters.business,
			options: businesses.map((business: any) => ({
				value: business._id,
				text: business.name,
			})),
			onChange: handleBusinessChange,
			minWidth: 170,
			maxWidth: 170,
			allowClear: true,
		})

		// Contract select
		actions.push({
			type: "select",
			text: Strings.dashboard.contract,
			placeholder: Strings.orders.selectContract,
			value: filters.contract,
			options: [
				...(allowAllContracts
					? [{ value: ALL_CONTRACTS_VALUE, text: Strings.routes.allContracts }]
					: []),
				...contracts.map((contract: any) => ({
					value: contract._id,
					text: contract.name || contract.type.name,
				})),
			],
			onChange: (contractId: string | null) =>
				handleContractChange(contractId, contracts),
			disabled: !filters.business,
			minWidth: 170,
			maxWidth: 170,
			margin: "left",
			allowClear: true,
		})

		// Date picker
		actions.push({
			type: "datePicker",
			isSingleDate: true,
			separator: "left",
			text: Strings.fields.date,
			dates: filters.date,
			onChange: handleDateChange,
			className: "fixedPicker",
			inputReadOnly: true,
		})

		return actions
	}, [
		businesses,
		contracts,
		filters.business,
		filters.contract,
		filters.date,
		handleBusinessChange,
		handleContractChange,
		handleDateChange,
		onDeleteModalSuccess,
		allowAllContracts,
	])

	const handleDelete = async () => {
		setModalVisible(false)
		dispatch(setLoader(true))

		try {
			const response = await API.delete({
				url: Endpoints.uriOrdersDevDelete(),
				data: {
					business: filters.business,
					contract: filters.contract,
					date: filters.date ? filters.date.format("YYYY-MM-DD") : null,
				},
			})

			if (response.ok) {
				notification.success({
					message: "Deleted",
					description:
						"All orders and routes for the selected criteria have been deleted.",
					placement: "bottomRight",
					duration: 5,
				})
				onDeleteModalSuccess?.()
			} else {
				notification.error({
					message: "Delete Failed",
					description: response?.data?.message || "Failed to delete.",
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (error) {
			notification.error({
				message: "Delete Failed",
				description: "An unexpected error occurred.",
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			dispatch(setLoader(false))
		}
	}

	// Delete confirmation modal element — rendered by the page
	const businessObj = businesses.find((b: any) => b._id === filters.business)
	const contractObj = contracts.find((c: any) => c._id === filters.contract)
	const formattedDate = filters.date ? filters.date.format("DD/MM/YYYY") : ""

	const deleteModal = onDeleteModalSuccess ? (
		<Modal
			title="Delete Confirmation"
			visible={modalVisible}
			onCancel={() => setModalVisible(false)}
			footer={[
				<Button key="cancel" onClick={() => setModalVisible(false)}>
					{Strings.generic.cancel}
				</Button>,
				<Button
					key="confirm"
					type="primary"
					danger
					onClick={handleDelete}
					disabled={
						!filters.business ||
						!filters.contract ||
						filters.contract === ALL_CONTRACTS_VALUE ||
						!filters.date
					}
				>
					Delete All!
				</Button>,
			]}
		>
			<div style={{ marginBottom: 16 }}>
				<strong>Warning: This action cannot be undone!</strong>
			</div>
			<p>
				You are about to delete <strong>ALL orders and routes</strong> for:
			</p>
			<ul style={{ marginLeft: 20 }}>
				<li>
					<strong>Business:</strong> {businessObj?.name || "Not selected"}
				</li>
				<li>
					<strong>Contract:</strong>{" "}
					{contractObj?.name || contractObj?.type?.name || "Not selected"}
				</li>
				<li>
					<strong>Date:</strong> {formattedDate || "Not selected"}
				</li>
			</ul>
			<p style={{ marginTop: 16, color: "#ff4d4f" }}>
				This will permanently delete all orders and routes matching these
				criteria. Are you sure you want to proceed?
			</p>
		</Modal>
	) : null

	return {
		businesses,
		contracts,
		filters,
		handleBusinessChange,
		handleContractChange,
		handleDateChange,
		breadcrumbActions,
		deleteModal,
		isLoading,
		refetch: fetchBusinesses,
	}
}
